import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Phone, Calendar, CheckCircle, Star, Clock, ArrowRight, Sparkles, Shield } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";

const LANDING_DATA = {
  "drywall-repair": {
    title: "Drywall Repair in Central Florida",
    subtitle: "Fast, clean drywall repair — patches, texturing, and full installation.",
    priceRange: "$150 – $1,500",
    features: [
      "Small patch repairs from $150",
      "Water damage restoration",
      "Complete drywall installation",
      "Texture matching (orange peel, knockdown, smooth)",
      "Same-week availability"
    ],
    testimonial: { name: "Maria S.", text: "Fixed our drywall and painted two rooms. Fast, clean work.", rating: 5 }
  },
  "flooring": {
    title: "Flooring Installation in Central Florida",
    subtitle: "Hardwood, laminate, vinyl plank, and tile flooring installed right.",
    priceRange: "$500 – $6,000+",
    features: [
      "LVP / vinyl plank from $3/sq ft",
      "Hardwood installation & refinishing",
      "Tile flooring for kitchens & baths",
      "Baseboard and trim included",
      "Free on-site measurement"
    ],
    testimonial: { name: "James R.", text: "Professional, on time, and the price was fair. Highly recommend.", rating: 5 }
  },
  "handyman-services": {
    title: "Handyman Services in Central Florida",
    subtitle: "One call handles it all — painting, repairs, installs, and more.",
    priceRange: "$75 – $5,000+",
    features: [
      "Drywall, painting, flooring, tile",
      "Door and window installation",
      "Cabinet and baseboard work",
      "Minor electrical and roof repair",
      "Licensed, insured, family-owned"
    ],
    testimonial: { name: "Linda T.", text: "Kitchen cabinets look amazing. Great team.", rating: 5 }
  }
};

const ServiceLandingPage = () => {
  const { slug } = useParams();
  const data = LANDING_DATA[slug];
  const [form, setForm] = useState({ name: "", phone: "", service: slug?.replace(/-/g, " ") || "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!data) {
    return (
      <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Service not found</h1>
          <Link to="/services" className="text-[#FF6B35] hover:underline">View all services</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please enter your name and phone");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contacts`, {
        name: form.name, phone: form.phone,
        email: "", message: `Landing page lead: ${form.service}`
      });
      setSubmitted(true);
      if (window._track) window._track("landing_lead", { service: form.service, page: slug });
    } catch {
      toast.error("Call us at 863-340-9244");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10] flex items-center justify-center" data-testid="landing-success">
        <div className="max-w-md mx-auto px-4 text-center">
          <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-3">We'll Call You Shortly!</h2>
          <p className="text-[#A0AAB2] mb-6">Our team is reviewing your request and will reach out within minutes.</p>
          <a href="tel:863-340-9244" className="block gradient-btn py-4 rounded-xl font-bold text-lg text-center">
            <Phone size={18} className="inline mr-2" />Or Call Now: 863-340-9244
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10]" data-testid="service-landing-page">
      {/* Hero */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-[#15161C] to-[#0B0C10]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Left: Content */}
            <div>
              <div className="inline-flex items-center space-x-2 bg-[#FF6B35]/20 text-[#FF6B35] px-3 py-1 rounded-full text-xs font-medium mb-4">
                <Sparkles size={12} />
                <span>Limited availability this week</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">{data.title}</h1>
              <p className="text-[#A0AAB2] text-base mb-6">{data.subtitle}</p>

              <div className="flex items-center space-x-3 mb-6">
                <span className="text-[#FF6B35] text-2xl font-bold">{data.priceRange}</span>
                <span className="text-[#A0AAB2] text-sm">estimated range</span>
              </div>

              <ul className="space-y-2.5 mb-8">
                {data.features.map((f, i) => (
                  <li key={i} className="flex items-start space-x-2.5">
                    <CheckCircle size={16} className="text-green-400 mt-0.5 shrink-0" />
                    <span className="text-white text-sm">{f}</span>
                  </li>
                ))}
              </ul>

              {/* Testimonial */}
              <div className="bg-[#1F2027] border border-[#2A2B32] rounded-xl p-4">
                <div className="flex text-yellow-400 mb-2">
                  {[...Array(data.testimonial.rating)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
                </div>
                <p className="text-white text-sm mb-2">"{data.testimonial.text}"</p>
                <p className="text-[#A0AAB2] text-xs">— {data.testimonial.name}</p>
              </div>
            </div>

            {/* Right: Lead Form */}
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-2xl p-6 md:p-8">
              <div className="text-center mb-5">
                <h2 className="text-xl font-bold text-white mb-1">Get Your Free Estimate</h2>
                <p className="text-[#A0AAB2] text-sm flex items-center justify-center space-x-1.5">
                  <Clock size={14} className="text-green-400" />
                  <span>We respond within minutes</span>
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3">
                <input type="text" value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3.5 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
                  placeholder="Your Name *" data-testid="landing-name" />
                <input type="tel" value={form.phone} onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-3.5 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
                  placeholder="Phone Number *" data-testid="landing-phone" />
                <button type="submit" disabled={submitting}
                  className="w-full gradient-btn py-4 rounded-xl font-bold text-lg disabled:opacity-50 shadow-lg shadow-[#FF6B35]/20"
                  data-testid="landing-submit">
                  {submitting ? "Sending..." : "Get My Free Estimate"}
                </button>
              </form>
              <div className="mt-4 space-y-2">
                {["100% free, no obligation", "Licensed & insured", "We call back in minutes"].map((t, i) => (
                  <div key={i} className="flex items-center space-x-2 justify-center">
                    <Shield size={12} className="text-green-400" />
                    <span className="text-[#A0AAB2] text-xs">{t}</span>
                  </div>
                ))}
              </div>
              <div className="text-center mt-4 pt-4 border-t border-[#2A2B32]">
                <a href="tel:863-340-9244" className="text-[#FF6B35] font-semibold text-sm hover:underline">
                  <Phone size={14} className="inline mr-1" />Or call: 863-340-9244
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceLandingPage;
